/**
 * Address Utilities
 * Reusable functions for building and formatting addresses
 */

/**
 * Format house number with suffix
 *
 * Rules:
 * - If no houseNumber and no suffix → null
 * - If suffix is empty → return houseNumber
 * - If suffix starts with - or / → no space: "33-3", "33/2"
 * - Else → space: "33 A", "33 bis"
 *
 * @param houseNumber Base house number
 * @param suffix House number suffix (toevoeging)
 * @returns Formatted house number with suffix, or null if both empty
 */
export function formatHouseNumber(
  houseNumber?: string | null,
  suffix?: string | null
): string | null {
  const trimmedNumber = houseNumber?.trim() || '';
  const trimmedSuffix = suffix?.trim() || '';

  // If no house number and no suffix → null
  if (!trimmedNumber && !trimmedSuffix) {
    return null;
  }

  // If no suffix → return just the number
  if (!trimmedSuffix) {
    return trimmedNumber || null;
  }

  // If no number but has suffix → return just suffix (edge case)
  if (!trimmedNumber) {
    return trimmedSuffix;
  }

  // If suffix starts with - or / → no space
  if (trimmedSuffix.startsWith('-') || trimmedSuffix.startsWith('/')) {
    return `${trimmedNumber}${trimmedSuffix}`;
  }

  // Default: space between number and suffix
  return `${trimmedNumber} ${trimmedSuffix}`;
}

interface AddressFields {
  addressLine1?: string | null;
  houseNumber?: string | null;
  houseNumberSuffix?: string | null;
  postalCode?: string | null;
  city?: string | null;
  country?: string | null;
}

/**
 * Build a formatted address string from customer/party fields
 * @param fields - Address fields from customer or party data
 * @returns Formatted address string or null if no address data
 */
export function buildAddressString(fields: AddressFields): string | null {
  const addressParts: string[] = [];

  // Street + house number + suffix
  if (fields.addressLine1) {
    const formattedHouseNumber = formatHouseNumber(fields.houseNumber, fields.houseNumberSuffix);
    const streetLine = formattedHouseNumber
      ? `${fields.addressLine1} ${formattedHouseNumber}`
      : fields.addressLine1;
    addressParts.push(streetLine);
  } else if (fields.houseNumber || fields.houseNumberSuffix) {
    // Edge case: house number without street
    const formattedHouseNumber = formatHouseNumber(fields.houseNumber, fields.houseNumberSuffix);
    if (formattedHouseNumber) {
      addressParts.push(formattedHouseNumber);
    }
  }

  // Postal code + city
  if (fields.postalCode || fields.city) {
    const cityLine = [fields.postalCode, fields.city].filter(Boolean).join(' ');
    addressParts.push(cityLine);
  }

  // Country (optional, often omitted for domestic)
  if (fields.country && fields.country !== 'Nederland' && fields.country !== 'Netherlands') {
    addressParts.push(fields.country);
  }

  return addressParts.length > 0 ? addressParts.join(', ') : null;
}

/**
 * Validate if address fields are complete
 * @param fields - Address fields to validate
 * @returns true if address has minimum required fields
 */
export function hasValidAddress(fields: AddressFields): boolean {
  return !!(fields.addressLine1 || fields.city);
}
